import { FC, ReactNode, useCallback, useRef, useState } from 'react'

import type { SerializedError } from '@reduxjs/toolkit'
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query'

import { LockedFeature } from '../../../../components/LockedFeature/LockedFeature'
import { useAppSelector } from '../../../../hooks/storeHooks'
import { LockedFeatureId } from '../../../../types/LockedFeature'
import { useRouteModal } from '../../../route-modals/hooks/useRouteModal'
import { EModalType } from '../../../route-modals/types/modalTypes'
import { EventDialogTab, resolveEventDialogTab } from '../../hooks/useEventDialogTabs'
import { useEventGameDataAccess } from '../../hooks/useEventGameDataAccess'
import { LiveEventGameAccess, useLiveEventGameAccess } from '../../hooks/useLiveEventGameAccess'
import { useTrackedEvent } from '../../hooks/useTrackedEvent'
import { useTrackedGames } from '../../hooks/useTrackedGames'
import { useDefaultLiveEvents } from '../../hooks/useTrackedGamesEvents'
import { selectHighlightedEvent } from '../../slices/liveEventModalSlice'
import { TrackedGame } from '../../types/TrackedGame'
import { TrackingEvent } from '../../types/TrackingEvents'
import { getHttpErrorStatus } from '../../utils/httpErrorStatus'
import { LiveEventsEventDialog } from './LiveEventsEventDialog'

export type LiveEventsEventDialogParams = {
  liveEventTypeId: string
  liveEventId: string
  trackedGameId: string
  durationStart?: string
  durationEnd?: string
  tab: EventDialogTab
  screenshotIndex: number
}

type LiveEventsEventDialogContainerProps = LiveEventsEventDialogParams & {
  children?: ReactNode
}

const initialScreenshotIndex = 0

const ACCESS_REFUSAL_STATUSES = [401, 403, 404]
const isAccessRefusal = (error: FetchBaseQueryError | SerializedError | undefined) => {
  const status = getHttpErrorStatus(error)
  return status !== undefined && ACCESS_REFUSAL_STATUSES.includes(status)
}

export const LiveEventsEventDialogContainer: FC<LiveEventsEventDialogContainerProps> = ({
  liveEventTypeId: eventTypeId,
  liveEventId: eventId,
  trackedGameId,
  durationStart,
  durationEnd,
  tab: requestedTab,
  screenshotIndex = initialScreenshotIndex,
  children,
}) => {
  const tab = resolveEventDialogTab(requestedTab)
  const { popModal, replaceModal } = useRouteModal()
  const gameAccess = useLiveEventGameAccess()
  // The access queries are shared with the feed, and RTK Query re-requests one that failed whenever a new subscriber
  // arrives (this container, for one). While that request is in flight the failure is gone from the hook result and
  // the query reads as loading again. Following it would blink the dialog and switch the event check off and on
  // again, so only the answer the queries settle on is used, and loading means the first load only.
  const settledAccessRef = useRef<Pick<LiveEventGameAccess, 'isGameLocked' | 'isError'>>()
  if (!gameAccess.isLoading) {
    settledAccessRef.current = { isGameLocked: gameAccess.isGameLocked, isError: gameAccess.isError }
  }
  const settledAccess = settledAccessRef.current
  const isGameAccessLoading = settledAccess === undefined
  // Locked by the plan, or the plan couldn't be read: either way the backend has the last word. Decided once per
  // event, on the first settled answer, so what happens to the access queries afterwards cannot change what is opened.
  const liveBackendCheck = settledAccess !== undefined && (settledAccess.isGameLocked(trackedGameId) || settledAccess.isError)
  const [backendCheck, setBackendCheck] = useState<{ eventId?: string; needed: boolean }>()
  if (settledAccess !== undefined && backendCheck?.eventId !== eventId) {
    setBackendCheck({ eventId, needed: liveBackendCheck })
  }
  const needsBackendCheck = backendCheck?.eventId === eventId ? backendCheck.needed : liveBackendCheck
  // Same request the dialog itself makes, so this shares its cache entry rather than adding one. It goes through
  // `useTrackedEvent` like the dialog does: a container that mounts again (or only starts checking) while the
  // entry already holds the backend's refusal reads it instead of requesting the event a second time.
  const lockedEventCheck = useTrackedEvent(eventId, { skip: !needsBackendCheck || !eventId })
  const isRefusedByBackend = lockedEventCheck.isError && isAccessRefusal(lockedEventCheck.error)
  // The backend check only decides what to open, once per event. The dialog subscribes to the same
  // query, and RTK Query re-requests an entry that failed whenever a new subscriber arrives, which
  // flips `isLoading` back on here. Following it would unmount the dialog, fail again, remount it,
  // and so on forever, so the check is latched as done after its first answer.
  const [checkedEventId, setCheckedEventId] = useState<string>()
  const isBackendCheckDone = checkedEventId === eventId
  if (needsBackendCheck && !isGameAccessLoading && !isBackendCheckDone && !lockedEventCheck.isUninitialized && !lockedEventCheck.isLoading) {
    setCheckedEventId(eventId)
  }
  const trackedGames = useTrackedGames({})
  // The game's events are requested only once the event check says they can be served; for a game the plan locks that
  // means after the backend has opened the event, so a refused link makes the one refused request and nothing else.
  const { canRequestGameData } = useEventGameDataAccess({ eventId, trackedGameId })
  const trackedEvents = useDefaultLiveEvents({ trackedGameIds: canRequestGameData ? [trackedGameId] : [] })
  const trackedGame = trackedGames.data?.find((trackedGame) => trackedGame.game.id === trackedGameId)
  const highlightedEventId = useAppSelector(selectHighlightedEvent)
  const resolvedDurationStart = durationStart !== undefined && Number.isFinite(+durationStart) ? +durationStart : undefined
  const resolvedDurationEnd = durationEnd !== undefined && Number.isFinite(+durationEnd) ? +durationEnd : undefined

  const handleTabChange = useCallback(
    (tab: EventDialogTab) => {
      replaceModal({
        type: EModalType.LIVE_EVENT,
        params: {
          trackedGameId: trackedGameId,
          liveEventTypeId: eventTypeId,
          liveEventId: eventId,
          durationStart: resolvedDurationStart,
          durationEnd: resolvedDurationEnd,
          tab: tab,
          screenshotIndex: +screenshotIndex,
        },
      })
    },
    [replaceModal, trackedGameId, eventTypeId, eventId, resolvedDurationStart, resolvedDurationEnd, screenshotIndex]
  )

  const handleEventChange = useCallback(
    (event: TrackingEvent, nextTab: EventDialogTab = EventDialogTab.Description) => {
      replaceModal({
        type: EModalType.LIVE_EVENT,
        params: {
          trackedGameId: trackedGameId,
          liveEventTypeId: event.typeId,
          liveEventId: event.eventId,
          durationStart: event.start,
          durationEnd: event.end,
          tab: nextTab,
          screenshotIndex: initialScreenshotIndex,
        },
      })
    },
    [replaceModal, trackedGameId]
  )

  const handleTrackedGameChange = useCallback(
    (trackedGame: TrackedGame) => {
      replaceModal({
        type: EModalType.LIVE_EVENT,
        params: {
          trackedGameId: trackedGame.game.id,
          liveEventTypeId: eventTypeId,
          tab: EventDialogTab.AllEvents,
          screenshotIndex: initialScreenshotIndex,
        },
      })
    },
    [eventTypeId, replaceModal]
  )

  const handleScreenshotIndexChange = useCallback(
    (index: number) => {
      replaceModal({
        type: EModalType.LIVE_EVENT,
        params: {
          trackedGameId,
          liveEventTypeId: eventTypeId,
          liveEventId: eventId,
          durationStart: resolvedDurationStart,
          durationEnd: resolvedDurationEnd,
          tab,
          screenshotIndex: index,
        },
      })
    },
    [replaceModal, trackedGameId, eventTypeId, eventId, resolvedDurationStart, resolvedDurationEnd, tab]
  )

  const handleClose = () => {
    popModal()
  }

  // A shared or bookmarked link can point at a game outside the user's plan. When the backend
  // refuses that event too, explain the lock instead of opening a dialog with nothing in it.
  // Any other failure falls through to the dialog, whose own error handling reports it.
  if (isGameAccessLoading || (needsBackendCheck && !isBackendCheckDone && lockedEventCheck.isLoading)) return null
  if (needsBackendCheck && isRefusedByBackend) {
    const lockedGame = trackedGame?.game
    return (
      <LockedFeature.Dialog
        lockedFeatureId={LockedFeatureId.LiveEventsGameUnlock}
        game={
          lockedGame
            ? { name: lockedGame.resolvedName, artist: lockedGame.artist || undefined, iconUrl: (lockedGame.getIcon() as string | undefined) || undefined }
            : undefined
        }
        open
        onClose={handleClose}
      />
    )
  }

  return (
    <LiveEventsEventDialog
      data={{
        eventId: eventId,
        eventTypeId: eventTypeId,
        trackedGame: trackedGame,
        tab: tab,
        highlightedEventId: highlightedEventId,
        durationStart: resolvedDurationStart,
        durationEnd: resolvedDurationEnd,
        screenshotIndex: +screenshotIndex,
        trackedEvents: trackedEvents.events,
        trackedGames: trackedGames.data,
        canRequestGameData,
      }}
      open
      onClose={handleClose}
      onEventChange={handleEventChange}
      onTabChange={handleTabChange}
      onTrackedGameChange={handleTrackedGameChange}
      onScreenshotIndexChange={handleScreenshotIndexChange}
    />
  )
}
